import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPriceDisplay } from '@/util/formatting';
import type { OrderServicePaneProps } from './OrderServiceSummary';

/**
 * The Order Service CTA stack — Add to Cart / Place Order Now on the main step,
 * Confirm & Pay / Back once paying. Split out of OrderServiceSummary so the
 * sub-lg sheet can pin it to a footer while the summary scrolls above it; the
 * desktop dialog renders it directly after the summary, unchanged.
 *
 * Renders nothing while the card LIST is open (that view owns its own Cancel),
 * so a host footer collapses instead of showing an empty bar.
 */
export default function OrderServiceActions({
  order,
  pay,
  step,
  setStep,
  payView,
  setPayView,
  effectiveTotal,
  effectiveFree,
  showCardUi,
  submitButtonLabel,
  onSubmit,
  onConfirmPay,
  onClose
}: OrderServicePaneProps) {
  if (step === 'main') {
    return (
      <>
        <Button
          className="mt-5 w-full"
          disabled={!order.canSubmit || order.submitting}
          onClick={onSubmit}
        >
          {order.submitting && (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          )}
          {submitButtonLabel}
        </Button>
        <div className="my-3 text-center text-xs uppercase text-muted-foreground">
          or
        </div>
        <Button
          variant="outline"
          className="w-full"
          data-testid="place-order-now"
          disabled={!order.canSubmit}
          onClick={() => {
            setPayView('current');
            setStep('paying');
          }}
        >
          {order.isFree
            ? 'Place Order Now'
            : `Place Order Now · $${formatPriceDisplay(order.subtotal)}`}
        </Button>
        {order.isEdit && (
          <Button
            variant="ghost"
            className="mt-3 w-full text-destructive hover:text-destructive"
            disabled={order.submitting}
            onClick={async () => {
              const ok = await order.removeLine();
              if (ok) onClose();
            }}
          >
            Remove from Cart
          </Button>
        )}
      </>
    );
  }

  if ((step === 'paying' || step === 'placing') && payView === 'current') {
    return (
      <>
        <Button
          className="mt-3 w-full"
          disabled={step === 'placing' || (showCardUi && !pay.selectedCard)}
          onClick={onConfirmPay}
        >
          {step === 'placing'
            ? 'Placing Order…'
            : effectiveFree
              ? 'Place Order'
              : `Confirm & Pay · $${formatPriceDisplay(effectiveTotal)}`}
        </Button>
        <button
          type="button"
          className="mt-2 w-full text-sm text-muted-foreground"
          onClick={() => setStep('main')}
        >
          Back
        </button>
      </>
    );
  }

  return null;
}
